"use client"

import { useRef, useState, useEffect } from "react"
import { Toaster } from "@/components/ui/toaster"
import { CandidateProfilePanel } from "@/components/candidate-profile-panel"
import { PageHeader } from "@/components/graphite/page-header"
import {
  AddingToBar,
  ResultsHeader,
  ResultsTable,
} from "@/components/search/graphite-search-results"
import { JobLinkDialog } from "@/components/job-link-dialog"
import { useSearchResultRows } from "@/hooks/use-search-result-rows"
import { useResultVerdicts } from "@/hooks/use-result-verdicts"
import { JobSearchContext } from "@/components/job-search-context"
import { JobSearchFilters } from "@/components/job-search-filters"
import { JobSearchBulkActions } from "@/components/job-search-bulk-actions"
import { SearchJobLinkProvider } from "@/contexts/search-job-link-context"
import { useGetJobs, useGetSearch, useGetSearchQueries, useGetSearchResultsHash } from "openapi/api/endpoints"
import { useSearchFilters } from "@/hooks/use-search-filter"
import { createRoute } from "@tanstack/react-router"
import MainAppRoute from "@/rootRoutes/MainApp"
import { Loader2 } from "lucide-react"
import { Pagination } from "@/components/search/search-pagination"
import { mapSearchConfigToFiltersDraft } from "@/utils/search-utils"
import { DEFAULT_SEARCH_FILTERS_DRAFT } from "@/lib/search-filter-model"
import { useCandidateActions } from "@/hooks/use-candidate-actions"
import { useWarmNextSearchResultsPage } from "@/hooks/use-warm-next-search-results-page"
import { useUrlSyncedSearchState } from "@/hooks/use-url-synced-search-state"

type SearchResultsSearch = {
  page: number
}

const SearchResultRoute = createRoute({
  getParentRoute: () => MainAppRoute,
  path: "/searches/$searchId/results",
  validateSearch: (search: Record<string, unknown>): SearchResultsSearch => {
    const rawPage = search.page
    const parsedPage =
      typeof rawPage === "number"
        ? rawPage
        : Number.parseInt(typeof rawPage === "string" ? rawPage : "", 10)

    return {
      page: Number.isInteger(parsedPage) && parsedPage > 0 ? parsedPage : 1,
    }
  },
  component: () => <SearchResultPage />,
})
export default SearchResultRoute

type jobCandidate = {
  id: string
  stage: string
  candidate_id: string
}

function SearchResultPage() {
  const navigate = SearchResultRoute.useNavigate()
  const { patchSearch } = useUrlSyncedSearchState<SearchResultsSearch>(navigate)
  const params = SearchResultRoute.useParams()
  const query = SearchResultRoute.useSearch()
  const searchHash = params.searchId
  const page = query.page
  const [currentPage, setCurrentPage] = useState(page)
  const resultsScrollRef = useRef<HTMLDivElement>(null)
  const [showHidden, setShowHidden] = useState(false)
  /* "Change", on the adding-to bar: the link dialog with no Yes waiting on it. */
  const [isChangingJob, setIsChangingJob] = useState(false)

  useEffect(() => {
    setCurrentPage(page)
  }, [page])

  const { data: searchTerms, error: isTermsError } = useGetSearchQueries({ hash: searchHash })

  const {
    data: candidatesData,
    isPending: isLoadingCandidates,
    isError: isCandidatesError,
  } = useGetSearchResultsHash(searchHash, { page: String(page) }, { query: { enabled: page > 0 } })
  const { data: searchItem } = useGetSearch()
  /*
    The same failure-safe resolution the verdict hook uses: `/search/queries`
    has already named this search, and the saved-search list is only a fallback.
    Reading the list alone meant a failed or stale `/search` left this query
    disabled, the page unaware of who was already on the job, and every row
    offering to add them again.
  */
  const currentSearchId =
    (searchTerms?.root_search_id as string | undefined) ??
    searchItem?.find((search) => search.normalized_query_hash === searchHash)?.id
  const { data: jobs } = useGetJobs({ format: "checkState", searchId: currentSearchId }, { query: { enabled: !!currentSearchId } })
  const job_candidates = (jobs as unknown as { job_candidates?: jobCandidate[] })?.job_candidates?.map(
    (job_candidate) => job_candidate.candidate_id,
  )

  const candidates = candidatesData?.p_candidates || []
  const { filters, setFilters, filteredCandidates } = useSearchFilters(candidates)

  useWarmNextSearchResultsPage({
    currentPage: page,
    enabled: !!candidatesData?.p_hash,
    searchHash,
  })

  const [selectedProfileCandidateId, setSelectedProfileCandidateId] = useState<string | null>(null)
  const [profilePanelOpen, setProfilePanelOpen] = useState(false)
  const [selectedCandidates, setSelectedCandidates] = useState<string[]>([])

  const { setAction, getCounts, actions, getAction } = useCandidateActions(searchTerms?.root_search_id as string)

  const { hiddenCount, maybeCount } = getCounts()

  /*
    What a Yes, a Maybe and a hide do — the job this search feeds, the pipeline
    write, and the link dialog for a search with no job yet. It lived inside
    every result card, so thirty rows mounted thirty copies of the jobs query
    and both mutations.
  */
  const verdicts = useResultVerdicts({
    searchHash,
    // The page already has the search's id; the saved-search list is a fallback.
    searchId: searchTerms?.root_search_id as string | undefined,
    setAction,
    getAction,
    addedIds: new Set((job_candidates ?? []).map(String)),
  })

  const visibleCandidates = showHidden
    ? filteredCandidates
    : filteredCandidates.filter((candidate) => {
        const id = String(candidate.profile?.id ?? "")
        return !actions.hidden.includes(id)
      })

  /*
    27a plots every candidate against one axis, so the rows are built for the
    page rather than by each row: two tracks on their own scales would look
    comparable while comparing nothing.
  */
  const { rows, axis } = useSearchResultRows({
    candidates: visibleCandidates,
    // Rank and axis come from the page as ranked, not from what survives filters.
    pageOrder: candidates,
    verdictOf: verdicts.verdictOf,
    addedIds: verdicts.addedOrOptimistic,
  })

  const profileIndex = rows.findIndex(
    (row) => row.candidateId === selectedProfileCandidateId,
  )

  const handleViewProfile = (candidateId: string) => {
    setSelectedProfileCandidateId(candidateId)
    setProfilePanelOpen(true)
  }

  const toggleCandidate = (candidateId: string) => {
    setSelectedCandidates((prev) =>
      prev.includes(candidateId) ? prev.filter((id) => id !== candidateId) : [...prev, candidateId],
    )
  }

  const handleBulkAction = (action: string) => {
    if (action === "assign_job") {
      setSelectedCandidates([])
    }
  }

  const handleStartOutreach = () => {
    console.log(`Starting outreach for ${selectedCandidates.length} candidates`)
  }

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage)
    patchSearch({ page: newPage })
    // The window does not scroll -- this route renders inside the ATS panel,
    // so the results column is its own scroll container.
    resultsScrollRef.current?.scrollTo({ top: 0, behavior: "smooth" })
  }

  const isError = isTermsError || isCandidatesError

  if (isError) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600">Error loading search results</h2>
          <p className="text-muted-foreground mt-2">Please try again later</p>
        </div>
      </div>
    )
  }

  const queryDisplay = searchTerms?.prompt ?? ""
  const searchDraft = searchTerms ? mapSearchConfigToFiltersDraft(searchTerms) : DEFAULT_SEARCH_FILTERS_DRAFT

  const searchParamsData = searchTerms
    ? {
        query: queryDisplay,
        filters: searchDraft,
      }
    : { query: "", filters: DEFAULT_SEARCH_FILTERS_DRAFT }

  return (
    <SearchJobLinkProvider>
      <div className="@container flex h-full min-h-0 flex-col">
        {/*
          The same band every other ATS page opens with, rather than a header of
          its own inside the scroll — which is why this was the one page whose
          title scrolled away as you read the results under it.
        */}
        {/* The title is the search. The count belongs to the strip below it,
            which is where 27a puts it — stating it twice on one screen invites
            the two to disagree. */}
        <PageHeader
          title={queryDisplay || "Search results"}
          back={{ to: "/searches", label: "Back to saved searches" }}
        />

        {/*
          27a's three strips, as page chrome under the header: what the page
          holds, the job every Yes lands in, and what a selection can do.
        */}
        <ResultsHeader
          count={visibleCandidates.length}
          maybeCount={maybeCount}
          hiddenCount={hiddenCount}
          showHidden={showHidden}
          onToggleHidden={() => setShowHidden(!showHidden)}
        />

        <AddingToBar
          jobTitle={verdicts.linkedJob?.title}
          isResolving={verdicts.isResolvingLink}
          onLink={() => setIsChangingJob(true)}
        />

        <JobSearchBulkActions
          selectedCount={selectedCandidates.length}
          selectedCandidates={selectedCandidates}
          onBulkAction={handleBulkAction}
          onStartOutreach={handleStartOutreach}
          onClear={() => setSelectedCandidates([])}
        />

        <div className="flex min-h-0 flex-1">
          <div ref={resultsScrollRef} className="min-w-0 flex-1 overflow-auto">
            {/* The search itself, and the filters over it — both padded, above
                the results, which are a full-bleed table. */}
            <div className="space-y-4 p-4 @2xl:p-[18px]">
              {searchTerms && (
                <JobSearchContext
                  searchParams={searchParamsData}
                  searchConfig={searchTerms}
                  onSearchUpdated={(nextSearchHash) => {
                    navigate({
                      to: "/searches/$searchId/results",
                      params: { searchId: nextSearchHash },
                      search: { page: 1 },
                    })
                  }}
                />
              )}

              <JobSearchFilters
                filters={filters}
                onFiltersChange={setFilters}
                onAdvancedFiltersChange={() => {}}
              />
            </div>

            {isLoadingCandidates ? (
              <div className="flex items-center justify-center py-12">
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="h-8 w-8 animate-spin text-brand-ink" />
                  <p className="text-sm text-muted-foreground">Loading candidates…</p>
                </div>
              </div>
            ) : (
              <>
                <ResultsTable
                  rows={rows}
                  axis={axis}
                  selected={selectedCandidates}
                  onToggleSelect={toggleCandidate}
                  onOpen={handleViewProfile}
                  onVerdict={verdicts.setVerdict}
                  isResolving={verdicts.isResolvingLink}
                  empty={
                    <p className="border-t px-4 py-10 text-center text-[12.5px] text-muted-foreground">
                      {hiddenCount > 0 && !showHidden
                        ? "Every result on this page is hidden."
                        : "No results on this page."}
                    </p>
                  }
                />

                {candidatesData && (
                  <div className="p-4 @2xl:p-[18px]">
                    <Pagination
                      currentPage={currentPage}
                      hasNextPage={candidatesData.nextPage || false}
                      totalResults={filteredCandidates.length}
                      onPageChange={handlePageChange}
                    />
                  </div>
                )}
              </>
            )}
          </div>

          {/*
            One dialog for both ways in: a Yes on a search with no job linked
            yet, and "Change" on the adding-to bar. The Yes carries a candidate
            with it, so the dialog names them and lands the add once the job is
            attached.
          */}
          <JobLinkDialog
            isOpen={Boolean(verdicts.pendingCandidateId) || isChangingJob}
            onClose={() => {
              verdicts.closeLinkDialog()
              setIsChangingJob(false)
            }}
            candidateName={
              rows.find((row) => row.candidateId === verdicts.pendingCandidateId)?.name
            }
            linkJobAndSearch={(jobId) => {
              void verdicts.linkJobAndAdd(jobId)
              setIsChangingJob(false)
            }}
          />

          {/*
            29a keeps the verdict and "result N of M" in the sheet's own header,
            so a page of results can be walked without closing it. The index is
            over the rows actually on screen, which is what the reader is
            walking — not the unfiltered page.
          */}
          <CandidateProfilePanel
            candidateId={selectedProfileCandidateId}
            searchHash={searchHash}
            isOpen={profilePanelOpen}
            onClose={() => setProfilePanelOpen(false)}
            axis={axis}
            verdict={
              selectedProfileCandidateId
                ? verdicts.verdictOf(selectedProfileCandidateId)
                : "none"
            }
            isAdded={
              selectedProfileCandidateId
                ? verdicts.addedOrOptimistic.has(selectedProfileCandidateId)
                : false
            }
            isResolving={verdicts.isResolvingLink}
            onVerdict={verdicts.setVerdict}
            position={profileIndex >= 0 ? profileIndex + 1 : undefined}
            total={rows.length}
            onPrevious={
              profileIndex > 0
                ? () => setSelectedProfileCandidateId(rows[profileIndex - 1].candidateId)
                : undefined
            }
            onNext={
              profileIndex >= 0 && profileIndex < rows.length - 1
                ? () => setSelectedProfileCandidateId(rows[profileIndex + 1].candidateId)
                : undefined
            }
          />
        </div>
      </div>
      <Toaster />
    </SearchJobLinkProvider>
  )
}
